import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { IS_ON_PREM } from '@utils/constants/app-constants';
import { GET_URL } from '@utils/api/endpoints';

export const MAINTENANCE_CHECK_QUERY_KEY = 'maintenanceCheck';

/**
 * Hook to trigger an API call on page load that will be intercepted by the
 * backend's maintenance middleware.
 *
 * When maintenance mode is enabled, the backend middleware returns 503 with
 * { detail: "maintenance" } for all non-admin requests. The axios interceptor
 * in auth-context.tsx catches this and redirects to /maintenance.
 *
 * This hook ensures an API call is made on every page load/refresh (with no caching)
 * so the maintenance mode check is always triggered.
 */
export const useCheckMaintenance = () => {
  const triggerMaintenanceCheck = async () => {
    // Call the /me endpoint to trigger the backend maintenance middleware
    // If maintenance mode is on, backend returns 503 which the interceptor handles
    const response = await Axios.get(GET_URL().me);
    return response.data;
  };

  return useQuery([MAINTENANCE_CHECK_QUERY_KEY], triggerMaintenanceCheck, {
    enabled: IS_ON_PREM,
    useErrorBoundary: false,
    staleTime: 0, // Always consider stale - fetch on every mount
    cacheTime: 0, // Don't cache - we want a fresh check every time
    retry: false, // Don't retry - if we get 503, the interceptor handles it
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true // Check when user returns to the app
  });
};
